import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router";

import { GuidedPanel } from "../guidance/GuidedPanel";
import { useGuidedMode } from "../guidance/guidedModeState";
import { navigationGroups, routeForPath } from "../navigation";
import { useTheme, type ThemePreference } from "../theme/themeState";
import { useUnitDisplay } from "../units/UnitDisplayContext";

const NARROW_NAVIGATION_QUERY = "(max-width: 1023px)";
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const navigationMarks: Readonly<Record<string, string>> = {
  "/app": "D",
  "/projects": "P",
  "/catalog": "C",
  "/fleet": "F",
  "/curves": "Cv",
  "/network": "N",
  "/scenarios": "S",
  "/simulation": "Sm",
  "/optimization": "O",
  "/results": "R",
  "/exports": "E",
  "/settings": "Se",
  "/users": "U",
  "/admin/curve-library": "Rv",
};

function setInert(element: HTMLElement | null, inert: boolean) {
  if (element === null) return;
  if (inert) element.setAttribute("inert", "");
  else element.removeAttribute("inert");
}

function useNarrowNavigation(): boolean {
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window.matchMedia === "function"
      ? window.matchMedia(NARROW_NAVIGATION_QUERY).matches
      : false,
  );

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia(NARROW_NAVIGATION_QUERY);
    const update = (event: MediaQueryListEvent) => setIsNarrow(event.matches);
    setIsNarrow(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  return isNarrow;
}

function RouteFocus() {
  const { pathname } = useLocation();

  useEffect(() => {
    const main = document.getElementById("main-content");
    const heading = main?.querySelector<HTMLElement>("h1");
    if (heading === undefined || heading === null) {
      main?.focus();
      return undefined;
    }

    const priorTabIndex = heading.getAttribute("tabindex");
    heading.setAttribute("tabindex", "-1");
    heading.focus();
    return () => {
      if (priorTabIndex === null) heading.removeAttribute("tabindex");
      else heading.setAttribute("tabindex", priorTabIndex);
    };
  }, [pathname]);

  return null;
}

function UnitSystemSelect() {
  const selectId = useId();
  const { setSystem, system } = useUnitDisplay();

  return (
    <label className="header-select" htmlFor={selectId}>
      <span>Units</span>
      <select
        id={selectId}
        value={system}
        onChange={(event) =>
          setSystem(event.target.value === "si" ? "si" : "us-customary")
        }
      >
        <option value="us-customary">US customary</option>
        <option value="si">SI</option>
      </select>
    </label>
  );
}

function ThemeSelect() {
  const selectId = useId();
  const { preference, setPreference } = useTheme();

  return (
    <label className="header-select" htmlFor={selectId}>
      <span>Theme</span>
      <select
        id={selectId}
        value={preference}
        onChange={(event) =>
          setPreference(event.target.value as ThemePreference)
        }
      >
        <option value="system">System</option>
        <option value="light">Light</option>
        <option value="dark">Dark</option>
      </select>
    </label>
  );
}

function GuidedModeToggle() {
  const { enabled, setEnabled } = useGuidedMode();

  return (
    <button
      className="guided-toggle"
      type="button"
      role="switch"
      aria-checked={enabled}
      onClick={() => setEnabled(!enabled)}
    >
      <span>Help</span>
      <span className="guided-toggle__track" aria-hidden="true">
        <span className="guided-toggle__thumb" />
      </span>
      <small>{enabled ? "Shown" : "Hidden"}</small>
    </button>
  );
}

interface AppShellProps {
  /** Auth integration can provide WorkspaceAccount here without changing shell layout. */
  account?: ReactNode;
  /** Visibility only; the routed page retains its independent authorization guard. */
  canManageUsers?: boolean;
  canReviewCurveLibrary?: boolean;
}

export function AppShell({
  account,
  canManageUsers = false,
  canReviewCurveLibrary = false,
}: AppShellProps) {
  const { pathname } = useLocation();
  const { enabled: guidedModeEnabled } = useGuidedMode();
  const [navigationOpen, setNavigationOpen] = useState(false);
  const isNarrow = useNarrowNavigation();
  const railRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const mobileHeaderRef = useRef<HTMLElement>(null);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const skipLinkRef = useRef<HTMLAnchorElement>(null);
  const priorPathnameRef = useRef(pathname);
  const priorNarrowRef = useRef(isNarrow);
  const activeRoute = routeForPath(pathname);

  const closeNavigation = useCallback((restoreFocus: boolean) => {
    setNavigationOpen(false);
    if (restoreFocus) {
      window.requestAnimationFrame(() => menuButtonRef.current?.focus());
    }
  }, []);

  useEffect(() => {
    if (priorPathnameRef.current === pathname) return;
    priorPathnameRef.current = pathname;
    setNavigationOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (priorNarrowRef.current === isNarrow) return;
    priorNarrowRef.current = isNarrow;
    setNavigationOpen(false);
  }, [isNarrow]);

  useEffect(() => {
    const rail = railRef.current;
    const mobileHeader = mobileHeaderRef.current;
    const workspace = workspaceRef.current;
    const skipLink = skipLinkRef.current;
    const backgroundIsHidden = isNarrow && navigationOpen;

    setInert(rail, isNarrow && !navigationOpen);
    setInert(mobileHeader, backgroundIsHidden);
    setInert(workspace, backgroundIsHidden);
    setInert(skipLink, backgroundIsHidden);

    if (!backgroundIsHidden || rail === null) return undefined;

    const priorOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() => {
      rail.querySelector<HTMLElement>(".app-rail__close")?.focus();
    });

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        closeNavigation(true);
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = Array.from(
        rail?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? [],
      ).filter(
        (element) =>
          !element.hasAttribute("disabled") &&
          element.getAttribute("aria-hidden") !== "true",
      );
      if (focusable.length === 0) {
        event.preventDefault();
        rail?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (
        event.shiftKey &&
        (document.activeElement === first || !rail?.contains(document.activeElement))
      ) {
        event.preventDefault();
        last?.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last || !rail?.contains(document.activeElement))
      ) {
        event.preventDefault();
        first?.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = priorOverflow;
      setInert(mobileHeader, false);
      setInert(workspace, false);
      setInert(skipLink, false);
    };
  }, [closeNavigation, isNarrow, navigationOpen]);

  const backgroundIsHidden = isNarrow && navigationOpen;

  return (
    <div className="app-shell">
      <a
        ref={skipLinkRef}
        className="skip-link"
        href="#main-content"
        aria-hidden={backgroundIsHidden || undefined}
      >
        Skip to main content
      </a>
      <header
        ref={mobileHeaderRef}
        className="app-mobile-header"
        aria-hidden={backgroundIsHidden || undefined}
      >
        <NavLink className="brand" to="/app" aria-label="Flow Wizard dashboard">
          <span className="brand__mark" aria-hidden="true">
            FW
          </span>
          <span>
            <strong>Flow Wizard</strong>
            <small>Water-transfer planning</small>
          </span>
        </NavLink>
        <button
          ref={menuButtonRef}
          className="navigation-toggle"
          type="button"
          aria-controls="workspace-navigation"
          aria-expanded={navigationOpen}
          onClick={(event) => {
            event.currentTarget.blur();
            setNavigationOpen(true);
          }}
        >
          <span aria-hidden="true">☰</span>
          Menu
        </button>
      </header>

      <button
        className={
          navigationOpen
            ? "app-drawer-backdrop app-drawer-backdrop--open"
            : "app-drawer-backdrop"
        }
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        aria-label="Close navigation"
        onClick={() => closeNavigation(true)}
      />

      <aside
        ref={railRef}
        id="workspace-navigation"
        className={navigationOpen ? "app-rail app-rail--open" : "app-rail"}
        role={isNarrow && navigationOpen ? "dialog" : undefined}
        aria-label="Workspace navigation"
        aria-modal={isNarrow && navigationOpen ? true : undefined}
        aria-hidden={isNarrow && !navigationOpen ? true : undefined}
        tabIndex={-1}
      >
        <div className="app-rail__inner">
          <div className="app-rail__header">
            <NavLink
              className="brand"
              to="/app"
              aria-label="Flow Wizard dashboard"
              onClick={() => setNavigationOpen(false)}
            >
              <span className="brand__mark" aria-hidden="true">
                FW
              </span>
              <span>
                <strong>Flow Wizard</strong>
                <small>Water-transfer planning</small>
              </span>
            </NavLink>
            <button
              className="app-rail__close"
              type="button"
              aria-label="Close navigation"
              onClick={() => closeNavigation(true)}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>

          <nav id="primary-navigation" className="rail-nav" aria-label="Primary navigation">
            {navigationGroups.map((group) => {
              const items = group.items.filter(
                (item) => (
                  (!('administratorsOnly' in item) || !item.administratorsOnly || canManageUsers) &&
                  (!('curatorsOnly' in item) || !item.curatorsOnly || canReviewCurveLibrary)
                ),
              );
              return (
                <section
                  key={group.label}
                  className="rail-nav__group"
                  aria-labelledby={`navigation-group-${group.label.toLowerCase()}`}
                >
                  <h2 id={`navigation-group-${group.label.toLowerCase()}`}>
                    {group.label}
                  </h2>
                  <ul>
                    {items.map(({ label, path }) => (
                      <li key={path}>
                        <NavLink
                          to={path}
                          end={path === "/app"}
                          className={({ isActive }) =>
                            isActive
                              ? "rail-nav__link is-active"
                              : "rail-nav__link"
                          }
                          onClick={() => setNavigationOpen(false)}
                        >
                          <span className="rail-nav__mark" aria-hidden="true">
                            {navigationMarks[path] ?? label.slice(0, 1)}
                          </span>
                          <span className="rail-nav__label">{label}</span>
                          <span className="rail-nav__state" aria-hidden="true">
                            Current
                          </span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </nav>

          <section className="app-rail__footer" aria-label="Workspace tools">
            <div className="app-rail__preferences">
              <UnitSystemSelect />
              <ThemeSelect />
              <GuidedModeToggle />
            </div>
            {account ?? <p className="app-rail__local-status">Local workspace</p>}
          </section>
        </div>
      </aside>

      <div
        ref={workspaceRef}
        className="app-workspace"
        aria-hidden={backgroundIsHidden || undefined}
      >
        <main id="main-content" className="app-main" tabIndex={-1}>
          <RouteFocus />
          {guidedModeEnabled && activeRoute !== undefined ? (
            <GuidedPanel content={activeRoute.guide} />
          ) : null}
          <Outlet />
        </main>
        <footer className="app-footer">
          <p>
            Design-assist workspace · Engineering review required ·{' '}
            <Link to="/privacy">Privacy policy</Link>
          </p>
        </footer>
      </div>
    </div>
  );
}
